import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useOutletContext } from "react-router-dom";
import useDeleteConfirm from "@/hooks/useDeleteConfirm";
import {
  Check,
  Copy,
  Key,
  Loader2,
  QrCode,
  RefreshCw,
  Smartphone,
  Power,
  CheckCircle2,
  ChevronRight,
  Plus,
  Pencil,
  Trash2,
} from "lucide-react";

const MAX_INSTANCE_DISPLAY_NAME = 15;

function StepCard({ index, title, subtitle, done, active, children }) {
  return (
    <div
      data-testid={`step-card-${index}`}
      className={`border rounded-lg p-5 md:p-6 transition-all ${
        done
          ? "border-cp-primary/40 bg-cp-primary/5"
          : active
            ? "border-white/10 bg-cp-elevated/40"
            : "border-white/[0.06] bg-cp-bg opacity-80"
      }`}
    >
      <div className="flex items-start gap-4">
        <div
          className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
            done ? "bg-cp-primary text-cp-bg" : active ? "bg-cp-elevated text-cp-primary-light border border-cp-primary/40" : "bg-cp-elevated text-slate-500"
          }`}
        >
          {done ? <Check className="h-5 w-5" strokeWidth={3} /> : index}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold text-lg tracking-tight">{title}</div>
          <div className="text-sm text-slate-400 mt-0.5">{subtitle}</div>
          <div className="mt-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

/** Estado do número em linguagem simples + bolinha de cor (sem termos técnicos). */
function InstanceStatusPill({ status }) {
  const s = String(status || "").toLowerCase();
  if (s === "connected") {
    return (
      <span className="inline-flex items-center gap-2 rounded-full border border-cp-primary/40 bg-cp-primary/10 px-3 py-1 text-sm font-medium text-cp-primary-light">
        <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-cp-primary-light shadow-[0_0_10px_rgba(34,197,94,0.45)]" aria-hidden />
        Conectado
      </span>
    );
  }
  if (s === "qr_pending") {
    return (
      <span className="inline-flex items-center gap-2 rounded-full border border-amber-500/35 bg-amber-500/10 px-3 py-1 text-sm font-medium text-amber-100">
        <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-amber-400" aria-hidden />
        Aguardando o QR
      </span>
    );
  }
  if (s === "connecting") {
    return (
      <span className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-sm font-medium text-cyan-100">
        <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-cyan-400 animate-pulse" aria-hidden />
        Conectando…
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-cp-elevated px-3 py-1 text-sm font-medium text-slate-400">
      <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-red-500/80" aria-hidden />
      Desconectado
    </span>
  );
}

const PLATFORMS = {
  kiwify: {
    name: "Kiwify",
    steps: [
      "Acesse o painel Kiwify e vá em Apps → Webhooks",
      "Clique em Criar webhook",
      "Cole a URL copiada acima no campo URL",
      "Selecione eventos: Compra aprovada, Compra pendente (PIX)",
      "Salve. O Kiwify enviará eventos automaticamente quando um PIX for gerado/pago",
    ],
  },
  hotmart: {
    name: "Hotmart",
    steps: [
      "Na Hotmart, vá em Ferramentas → Webhook",
      "Clique em Novo webhook e cole a URL",
      "Ative os eventos: PURCHASE_COMPLETE e PURCHASE_BILLET_PRINTED",
      "Teste o webhook usando o botão Enviar teste",
      "Ative em produção",
    ],
  },
  cartpanda: {
    name: "Cartpanda",
    steps: [
      "Painel Cartpanda → Integrações → Webhooks",
      "Criar novo webhook e cole a URL do ConvertPix",
      "Selecione eventos: order.created (PIX gerado) e order.paid",
      "Ative o webhook",
    ],
  },
  ticto: {
    name: "Ticto",
    steps: [
      "Ticto → Integrações → Webhooks → Novo",
      "Cole a URL do ConvertPix",
      "Marque os eventos de Compra criada (aguardando pagamento) e Compra aprovada",
      "Salve. Pronto",
    ],
  },
  generic: {
    name: "Outros",
    steps: [
      "Qualquer checkout que permita webhooks pode integrar",
      "Cole a URL no campo de webhook da plataforma",
      "Configure pra enviar POST com JSON nos eventos de PIX gerado e pago",
      "O payload deve seguir o formato abaixo (veja \"Ver payload JSON esperado\")",
    ],
  },
};

function PlatformGuide() {
  const [active, setActive] = useState("kiwify");
  const current = PLATFORMS[active];
  return (
    <div className="border border-white/10 rounded-md bg-cp-bg overflow-hidden">
      <div className="flex flex-wrap border-b border-white/10">
        {Object.entries(PLATFORMS).map(([k, p]) => (
          <button
            key={k}
            onClick={() => setActive(k)}
            data-testid={`platform-tab-${k}`}
            className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition ${
              active === k ? "border-cp-primary text-cp-primary-light" : "border-transparent text-slate-500 hover:text-slate-200"
            }`}
          >
            {p.name}
          </button>
        ))}
      </div>
      <ol className="p-4 space-y-2.5">
        {current.steps.map((s, i) => (
          <li key={i} className="flex gap-3 text-sm text-slate-200">
            <span className="h-5 w-5 rounded-full bg-cp-elevated border border-white/10 text-slate-400 text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
              {i + 1}
            </span>
            <span className="leading-relaxed">{s}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function ConnectionPage() {
  const ctx = useOutletContext();
  const { confirmDelete, deleteConfirmDialog } = useDeleteConfirm();
  const reloadParent = ctx?.reloadStatus;
  const [tenant, setTenant] = useState(null);
  const [tenantLoadError, setTenantLoadError] = useState("");
  const [evoUrl, setEvoUrl] = useState("");
  const [evoKey, setEvoKey] = useState("");
  const [savingEvo, setSavingEvo] = useState(false);

  const [instances, setInstances] = useState([]);
  const instancesRef = useRef([]);
  const [newInstanceName, setNewInstanceName] = useState("");
  const [addingInstance, setAddingInstance] = useState(false);
  const [qr, setQr] = useState(null);
  const [pairing, setPairing] = useState(null);
  const [pairingInstanceId, setPairingInstanceId] = useState(null);
  const [connectingId, setConnectingId] = useState(null);
  const [editInstanceId, setEditInstanceId] = useState(null);
  const [editDisplayName, setEditDisplayName] = useState("");
  const [savingSendSettings, setSavingSendSettings] = useState(false);
  const [sendMode, setSendMode] = useState("fixed");
  const [sendFixedInstanceId, setSendFixedInstanceId] = useState("");

  const loadTenant = useCallback(async () => {
    try {
      const { data } = await api.get("/tenant");
      setTenant(data);
      setTenantLoadError("");
      setEvoUrl(data.evolution_url || "");
      setSendMode(data.send_mode || "fixed");
      setSendFixedInstanceId(data.send_fixed_instance_id || "");
    } catch (e) {
      setTenantLoadError(e?.response?.data?.detail || "Falha ao carregar configurações da conexão.");
      setTenant(null);
    }
  }, []);

  const loadInstances = useCallback(async () => {
    try {
      const { data } = await api.get("/whatsapp/instances");
      const list = Array.isArray(data?.instances) ? data.instances : [];
      setInstances(list);
      instancesRef.current = list;
    } catch {
      setInstances([]);
      instancesRef.current = [];
    }
  }, []);

  useEffect(() => {
    instancesRef.current = instances;
  }, [instances]);

  useEffect(() => {
    loadTenant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!tenant?.evolution_ready) return;
    void loadInstances();
  }, [tenant?.evolution_ready, loadInstances]);

  useEffect(() => {
    if (!tenant?.evolution_ready) return undefined;
    const tick = async () => {
      const list = instancesRef.current;
      const pending = list.filter((i) => i.status === "qr_pending" || i.status === "connecting");
      if (!pending.length) return;
      await Promise.all(pending.map((i) => api.get(`/whatsapp/instances/${i.id}/status`).catch(() => null)));
      await loadInstances();
      const after = instancesRef.current;
      if (after.some((i) => i.id === pairingInstanceId && i.status === "connected")) {
        setQr(null);
        setPairing(null);
        setPairingInstanceId(null);
        toast.success("Seu WhatsApp está conectado.");
        await loadTenant();
        reloadParent?.();
      }
    };
    tick();
    const id = setInterval(tick, 3500);
    return () => clearInterval(id);
  }, [tenant?.evolution_ready, loadInstances, loadTenant, pairingInstanceId, reloadParent]);

  const saveEvolution = async () => {
    setSavingEvo(true);
    try {
      const { data } = await api.put("/evolution/config", {
        evolution_url: evoUrl,
        evolution_api_key: evoKey,
      });
      setTenant(data);
      setEvoKey("");
      toast.success("Servidor conectado com sucesso!");
      reloadParent?.();
      await loadInstances();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar");
    }
    setSavingEvo(false);
  };

  const addInstance = async () => {
    const name = newInstanceName.trim().slice(0, MAX_INSTANCE_DISPLAY_NAME);
    if (!name) {
      toast.error("Indique um nome para o WhatsApp.");
      return;
    }
    setAddingInstance(true);
    try {
      await api.post("/whatsapp/instances", { display_name: name });
      setNewInstanceName("");
      toast.success("Número adicionado. Toque em «Ler QR Code» no cartão abaixo para conectar.");
      await loadInstances();
      reloadParent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao adicionar");
    }
    setAddingInstance(false);
  };

  const connectInstance = async (id) => {
    setConnectingId(id);
    setPairingInstanceId(id);
    try {
      const { data } = await api.post(`/whatsapp/instances/${id}/connect`);
      setQr(data.qr_base64);
      setPairing(data.pairing_code);
      if (data.qr_base64) {
        toast.success("QR Code gerado. Escaneie com o WhatsApp.");
      } else if (data.pairing_code) {
        toast.success("Código de pareamento gerado.");
      } else {
        toast.error("Não foi possível obter o QR agora. Tente novamente em alguns segundos.");
      }
      await loadInstances();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao gerar QR");
    }
    setConnectingId(null);
  };

  const disconnectInstance = async (id) => {
    try {
      await api.post(`/whatsapp/instances/${id}/disconnect`);
      if (pairingInstanceId === id) {
        setQr(null);
        setPairing(null);
        setPairingInstanceId(null);
      }
      toast.success("Número desligado. Pode ligar de novo quando quiser.");
      await loadInstances();
      await loadTenant();
      reloadParent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao desligar");
    }
  };

  const deleteInstance = async (id) => {
    if (instances.length <= 1) return;
    const ok = await confirmDelete({
      title: "Remover número WhatsApp",
      message: "Remover este número da sua conta? Você pode adicionar de novo depois, mas precisará ler o QR outra vez.",
    });
    if (!ok) return;
    try {
      await api.delete(`/whatsapp/instances/${id}`);
      if (pairingInstanceId === id) {
        setQr(null);
        setPairing(null);
        setPairingInstanceId(null);
      }
      toast.success("Número removido.");
      await loadInstances();
      await loadTenant();
      reloadParent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao remover");
    }
  };

  const startEditName = (inst) => {
    setEditInstanceId(inst.id);
    setEditDisplayName(inst.display_name || "");
  };

  const cancelEditName = () => {
    setEditInstanceId(null);
    setEditDisplayName("");
  };

  const saveDisplayName = async (id) => {
    const name = editDisplayName.trim().slice(0, MAX_INSTANCE_DISPLAY_NAME);
    if (!name) {
      toast.error("O nome é obrigatório.");
      return;
    }
    try {
      await api.patch(`/whatsapp/instances/${id}`, { display_name: name });
      setEditInstanceId(null);
      toast.success("Nome salvo.");
      await loadInstances();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao guardar");
    }
  };

  const saveSendSettings = async () => {
    if (sendMode === "fixed" && !sendFixedInstanceId) {
      toast.error("Escolha qual número envia as mensagens quando usar «sempre o mesmo».");
      return;
    }
    setSavingSendSettings(true);
    try {
      const { data } = await api.put("/tenant/whatsapp-send-settings", {
        send_mode: sendMode,
        send_fixed_instance_id: sendMode === "fixed" ? sendFixedInstanceId : null,
      });
      setTenant((prev) => (prev ? { ...prev, send_mode: data.send_mode, send_fixed_instance_id: data.send_fixed_instance_id } : data));
      setSendMode(data.send_mode || "fixed");
      setSendFixedInstanceId(data.send_fixed_instance_id || "");
      toast.success("Configuração salva.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao guardar");
    }
    setSavingSendSettings(false);
  };

  const regenerateWebhook = async () => {
    try {
      const { data } = await api.post("/tenant/regenerate-webhook-token");
      setTenant(data);
      toast.success("Novo token gerado. Atualize seu checkout!");
    } catch {
      toast.error("Falha");
    }
  };

  const copy = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Copiado!");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  if (!tenant) {
    if (tenantLoadError) {
      return (
        <div className="space-y-3">
          <p className="text-sm text-red-300">{tenantLoadError}</p>
          <button
            type="button"
            onClick={() => void loadTenant()}
            className="rounded-lg border border-white/15 px-3 py-2 text-sm text-slate-200 hover:bg-cp-elevated"
          >
            Tentar novamente
          </button>
        </div>
      );
    }
    return <div className="text-slate-500 text-sm">Carregando…</div>;
  }

  const evoManaged = Boolean(tenant.evolution_managed);
  const evoDone = evoManaged ? true : Boolean(tenant.evolution_ready);
  const whatsappDone = instances.some((i) => i.status === "connected");
  const webhookDone = true;

  const connectedInstances = instances.filter((i) => i.status === "connected");

  return (
    <div className="space-y-6 max-w-3xl" data-testid="connection-page">
      {deleteConfirmDialog}
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">Conectar em 3 passos</h1>
        <p className="text-slate-400 text-sm mt-2">
          Em poucos minutos seu motor de recuperação vai estar disparando mensagens automáticas.
        </p>
      </div>

      <StepCard
        index={1}
        title={evoManaged ? "Integração de mensagens" : "Servidor de mensagens"}
        subtitle={
          evoManaged
            ? "A ligação ao serviço de mensagens está ativa no servidor. Avance para associar um ou mais números WhatsApp."
            : "Indique o endereço e a chave do seu servidor para o ConvertPix enviar mensagens pelo WhatsApp."
        }
        done={evoDone}
        active={!evoDone}
      >
        {evoManaged ? (
          <div
            className="text-sm text-slate-200 bg-cp-elevated/60 border border-cp-primary/35 rounded-md p-4"
            data-testid="evolution-managed-notice"
          >
            <p className="text-cp-primary-light font-semibold mb-1">Pronto para parear</p>
            <p className="text-slate-400 leading-relaxed">
              Não precisa configurar nada aqui neste passo. No passo seguinte pode adicionar vários números WhatsApp
              (cada um com o seu QR Code) para distribuir envios e reduzir risco de bloqueio.
            </p>
          </div>
        ) : evoDone ? (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm">
              <div className="text-slate-400">URL</div>
              <code className="text-cp-primary-light font-mono text-xs break-all">{tenant.evolution_url}</code>
            </div>
            <button
              type="button"
              onClick={() => {
                setEvoKey("");
                setEvoUrl(tenant.evolution_url);
              }}
              data-testid="edit-evolution-btn"
              className="text-xs text-slate-400 hover:text-white underline"
            >
              Editar
            </button>
          </div>
        ) : null}
        {!evoManaged && (
          <>
            <div className={`${evoDone ? "mt-4 pt-4 border-t border-white/10" : ""} grid md:grid-cols-2 gap-3`}>
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">URL do servidor</label>
                <input
                  value={evoUrl}
                  onChange={(e) => setEvoUrl(e.target.value)}
                  placeholder="https://mensagens.seudominio.com"
                  data-testid="evolution-url-input"
                  className="w-full bg-cp-bg border border-white/10 focus:border-cp-primary focus:ring-2 focus:ring-cp-primary/30 outline-none text-sm px-3 py-2.5 rounded-md font-mono"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">
                  API Key {tenant.evolution_api_key_set && <span className="text-cp-primary-light">· salva ✓</span>}
                </label>
                <input
                  type="password"
                  value={evoKey}
                  onChange={(e) => setEvoKey(e.target.value)}
                  placeholder={tenant.evolution_api_key_set ? "••••••••  (deixe em branco para manter)" : "sua API Key global"}
                  data-testid="evolution-apikey-input"
                  className="w-full bg-cp-bg border border-white/10 focus:border-cp-primary focus:ring-2 focus:ring-cp-primary/30 outline-none text-sm px-3 py-2.5 rounded-md font-mono"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={saveEvolution}
              disabled={savingEvo || !evoUrl || (!evoKey && !tenant.evolution_api_key_set)}
              data-testid="save-evolution-btn"
              className="mt-4 bg-cp-primary hover:bg-cp-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-cp-bg font-bold px-5 py-2.5 rounded-md transition-all inline-flex items-center gap-2 text-sm"
            >
              {savingEvo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Key className="h-4 w-4" />}
              {evoDone ? "Atualizar credenciais" : "Salvar e continuar"}
            </button>
            <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
              Modo avançado: servidor próprio. Se a equipa já configurou as credenciais no backend, este passo pode
              aparecer como concluído automaticamente.
            </p>
          </>
        )}
      </StepCard>

      <StepCard
        index={2}
        title="Seus números de WhatsApp"
        subtitle="Adicione um nome, conecte pelo QR e defina como as mensagens de recuperação são enviadas — em poucos toques."
        done={whatsappDone}
        active={evoDone && !whatsappDone}
      >
        {!evoDone ? (
          <div className="rounded-xl border border-white/10 bg-cp-elevated/30 p-4 text-sm text-slate-400">
            Conclua o passo anterior para liberar esta etapa.
          </div>
        ) : (
          <div className="space-y-10">
            {/* Bloco 1 — Adicionar */}
            <section className="rounded-2xl border border-white/[0.08] bg-cp-elevated/25 p-5 md:p-6 space-y-4" aria-labelledby="wa-add-heading">
              <h3 id="wa-add-heading" className="text-base font-semibold text-white tracking-tight">
                Adicionar novo WhatsApp
              </h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Dê um nome para identificar esse número no painel (ex.: <span className="text-slate-300">Vendas</span>,{" "}
                <span className="text-slate-300">Suporte</span>, <span className="text-slate-300">Recuperação 01</span>).
              </p>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="min-w-0 flex-1">
                  <label htmlFor="new-instance-name-input" className="sr-only">
                    Nome do WhatsApp
                  </label>
                  <input
                    id="new-instance-name-input"
                    value={newInstanceName}
                    onChange={(e) => setNewInstanceName(e.target.value.slice(0, MAX_INSTANCE_DISPLAY_NAME))}
                    maxLength={MAX_INSTANCE_DISPLAY_NAME}
                    placeholder="Ex.: Vendas, Suporte…"
                    data-testid="new-instance-name-input"
                    className="w-full rounded-xl border border-white/10 bg-cp-bg px-4 py-3 text-sm text-slate-100 outline-none transition focus:border-cp-primary/50 focus:ring-2 focus:ring-cp-primary/25"
                  />
                </div>
                <button
                  type="button"
                  onClick={addInstance}
                  disabled={addingInstance}
                  data-testid="add-instance-btn"
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-cp-primary px-5 py-3 text-sm font-bold text-cp-bg shadow-lg shadow-cp-primary/20 transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-45"
                >
                  {addingInstance ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  Adicionar número
                </button>
              </div>
            </section>

            {/* Bloco 2 — Lista de cartões */}
            <section className="space-y-4" aria-labelledby="wa-list-heading">
              <div className="flex items-end justify-between gap-3">
                <h3 id="wa-list-heading" className="text-base font-semibold text-white tracking-tight">
                  Seus WhatsApps
                </h3>
              </div>
              <div className="space-y-4" data-testid="whatsapp-instances-list">
                {instances.length === 0 ? (
                  <p className="text-sm text-slate-500">Carregando seus números…</p>
                ) : (
                  instances.map((inst) => {
                    const isPairing = pairingInstanceId === inst.id && (qr || pairing);
                    const isConnected = inst.status === "connected";
                    return (
                      <article
                        key={inst.id}
                        className={`rounded-2xl border p-5 transition-shadow ${
                          isConnected
                            ? "border-cp-primary/35 bg-cp-primary/[0.06] shadow-[0_0_0_1px_rgba(34,197,94,0.12)]"
                            : "border-white/[0.08] bg-cp-bg/90"
                        }`}
                        data-testid={`whatsapp-instance-row-${inst.id}`}
                      >
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0 flex-1 space-y-2">
                            {editInstanceId === inst.id ? (
                              <div className="flex flex-wrap items-center gap-2">
                                <input
                                  value={editDisplayName}
                                  onChange={(e) => setEditDisplayName(e.target.value.slice(0, MAX_INSTANCE_DISPLAY_NAME))}
                                  maxLength={MAX_INSTANCE_DISPLAY_NAME}
                                  className="min-w-[200px] flex-1 rounded-lg border border-white/10 bg-cp-bg px-3 py-2 text-sm text-white"
                                  data-testid={`instance-edit-name-${inst.id}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => saveDisplayName(inst.id)}
                                  className="rounded-lg bg-cp-primary/90 px-3 py-2 text-xs font-semibold text-cp-bg"
                                >
                                  Salvar nome
                                </button>
                                <button type="button" onClick={cancelEditName} className="px-2 py-2 text-xs text-slate-500 hover:text-slate-300">
                                  Cancelar
                                </button>
                              </div>
                            ) : (
                              <div className="flex flex-wrap items-center gap-2">
                                <h4 className="truncate text-lg font-bold text-white">{inst.display_name || "WhatsApp"}</h4>
                                <button
                                  type="button"
                                  onClick={() => startEditName(inst)}
                                  className="rounded-lg p-1.5 text-slate-500 transition hover:bg-white/5 hover:text-cp-primary-light"
                                  aria-label="Editar nome"
                                  data-testid={`instance-edit-btn-${inst.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                            <p className="text-sm text-slate-400">
                              Número:{" "}
                              <span className="font-medium text-slate-200 tabular-nums">{inst.phone ? inst.phone : "ainda não identificado"}</span>
                            </p>
                            <div className="pt-0.5">
                              <InstanceStatusPill status={inst.status} />
                            </div>
                          </div>
                        </div>

                        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                          <button
                            type="button"
                            onClick={() => connectInstance(inst.id)}
                            disabled={connectingId === inst.id}
                            data-testid={`instance-qr-btn-${inst.id}`}
                            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-cp-primary py-3 text-sm font-bold text-cp-bg shadow-md transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-[220px] sm:flex-none"
                          >
                            {connectingId === inst.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}
                            {isConnected ? "Gerar novo QR (reconectar)" : "Gerar QR Code e conectar"}
                          </button>
                          <button
                            type="button"
                            onClick={() => disconnectInstance(inst.id)}
                            data-testid={`instance-disconnect-btn-${inst.id}`}
                            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-white/10 py-3 text-sm font-medium text-slate-300 transition hover:bg-white/5 sm:min-w-[140px] sm:flex-none"
                          >
                            <Power className="h-4 w-4" />
                            Desligar
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteInstance(inst.id)}
                            disabled={instances.length <= 1}
                            data-testid={`instance-delete-btn-${inst.id}`}
                            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-red-500/25 py-3 text-sm font-medium text-red-300/95 transition hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-30 sm:min-w-[120px] sm:flex-none"
                          >
                            <Trash2 className="h-4 w-4" />
                            Remover
                          </button>
                        </div>

                        {isPairing && (
                          <div className="mt-6 grid gap-6 rounded-xl border border-white/10 bg-black/20 p-4 md:grid-cols-[auto,1fr]">
                            {qr ? (
                              <div className="mx-auto w-fit rounded-xl bg-white p-3 shadow-lg md:mx-0" data-testid="qr-code-container">
                                <img
                                  src={qr.startsWith("data:") ? qr : `data:image/png;base64,${qr}`}
                                  alt="QR Code para conectar o WhatsApp"
                                  className="h-44 w-44"
                                />
                              </div>
                            ) : (
                              <div className="max-w-[240px] rounded-lg border border-white/10 p-3 text-xs text-slate-400">
                                Não foi possível mostrar a imagem do QR. Use o código abaixo ou toque em «Conectar de novo».
                              </div>
                            )}
                            <div className="min-w-0 space-y-3">
                              <p className="text-sm font-medium text-white">No celular</p>
                              <ol className="list-decimal space-y-2 pl-4 text-sm text-slate-300">
                                <li>Abra o WhatsApp</li>
                                <li>
                                  Menu <span className="text-slate-400">(⋮)</span> → <strong className="text-slate-200">Aparelhos conectados</strong>
                                </li>
                                <li>
                                  <strong className="text-slate-200">Conectar um aparelho</strong> e aponte a câmera para o QR
                                </li>
                              </ol>
                              {!qr && pairing && (
                                <div className="rounded-lg border border-white/10 bg-cp-elevated/80 p-3">
                                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Código numérico</div>
                                  <div className="mt-1 break-all font-mono text-sm text-cp-primary-light">{pairing}</div>
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => connectInstance(inst.id)}
                                className="text-xs font-medium text-slate-400 underline-offset-2 hover:text-white hover:underline"
                                data-testid="refresh-qr-btn"
                              >
                                Gerar outro QR Code
                              </button>
                            </div>
                          </div>
                        )}
                      </article>
                    );
                  })
                )}
              </div>
            </section>

            {/* Bloco 3 — Envio automático */}
            <section
              className="rounded-2xl border border-white/[0.08] bg-cp-elevated/20 p-5 md:p-6 space-y-5"
              aria-labelledby="wa-send-heading"
              data-testid="whatsapp-send-settings"
            >
              {/* Header da seção */}
              <div className="space-y-2">
                <h3 id="wa-send-heading" className="text-lg font-bold text-white tracking-tight">
                  Escolha como o sistema vai enviar suas mensagens automáticas
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed">
                  Isso vale apenas para as mensagens automáticas de recuperação de PIX.
                </p>
              </div>

              {/* Opções de envio */}
              <div className="grid gap-4" data-testid="send-mode-select" role="radiogroup" aria-label="Modo de envio">
                {[
                  {
                    value: "fixed",
                    title: "Enviar sempre pelo mesmo número",
                    body: "Todas as mensagens automáticas serão enviadas sempre pelo mesmo WhatsApp.",
                  },
                  {
                    value: "round_robin",
                    title: "Revezar entre os números conectados",
                    body: "O sistema distribui as mensagens entre seus WhatsApps conectados.",
                    tag: "Recomendado",
                  },
                  {
                    value: "random",
                    title: "Sortear um número a cada envio",
                    body: "Cada mensagem pode sair de um WhatsApp diferente, escolhido aleatoriamente.",
                  },
                ].map((opt) => {
                  const selected = sendMode === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      data-testid={`send-mode-option-${opt.value}`}
                      onClick={() => setSendMode(opt.value)}
                      className={`relative w-full rounded-xl border-2 p-5 text-left transition-all ${
                        selected
                          ? "border-cp-primary bg-cp-primary/10 shadow-lg shadow-cp-primary/20"
                          : "border-white/[0.08] bg-cp-bg/40 hover:border-white/15 hover:bg-cp-bg/60"
                      }`}
                    >
                      {/* Indicador visual de seleção */}
                      {selected && (
                        <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-cp-primary flex items-center justify-center">
                          <Check className="w-4 h-4 text-cp-bg" />
                        </div>
                      )}
                      
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <span className={`text-base font-bold ${selected ? "text-cp-primary-light" : "text-slate-100"}`}>
                          {opt.title}
                        </span>
                        {opt.tag && (
                          <span className="shrink-0 rounded-lg bg-cp-primary/30 px-3 py-1 text-xs font-bold uppercase tracking-wide text-cp-primary-light">
                            {opt.tag}
                          </span>
                        )}
                      </div>
                      <p className={`text-sm leading-relaxed ${selected ? "text-slate-300" : "text-slate-400"}`}>
                        {opt.body}
                      </p>
                    </button>
                  );
                })}
              </div>

              {/* Campo condicional para modo fixo */}
              {sendMode === "fixed" && (
                <div className="p-5 rounded-xl bg-cp-primary/5 border border-cp-primary/20 space-y-3">
                  <label htmlFor="send-fixed-instance-select" className="block text-sm font-bold text-slate-200">
                    Escolha o número que será usado
                  </label>
                  {connectedInstances.length === 0 ? (
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <span className="text-amber-400">⚠️</span>
                      <p className="text-sm text-amber-200/90">
                        Conecte pelo menos um número acima para poder escolher.
                      </p>
                    </div>
                  ) : (
                    <select
                      id="send-fixed-instance-select"
                      value={sendFixedInstanceId}
                      onChange={(e) => setSendFixedInstanceId(e.target.value)}
                      data-testid="send-fixed-instance-select"
                      className="w-full rounded-lg border border-white/10 bg-cp-bg px-4 py-3.5 text-sm text-slate-100 outline-none focus:border-cp-primary/50 focus:ring-2 focus:ring-cp-primary/20"
                    >
                      <option value="">Selecione um número...</option>
                      {connectedInstances.map((i) => (
                        <option key={i.id} value={i.id}>
                          {i.display_name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Resumo visual da configuração */}
              <div className="p-4 rounded-lg bg-slate-800/30 border border-white/5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-cp-primary/10 flex items-center justify-center shrink-0">
                    <span className="text-lg">📋</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-300 mb-1">Resumo da configuração:</p>
                    <p className="text-sm text-slate-400">
                      {sendMode === "fixed" && "Enviando sempre pelo mesmo número"}
                      {sendMode === "round_robin" && "Revezando entre os números conectados"}
                      {sendMode === "random" && "Sorteando um número a cada envio"}
                      {sendMode === "fixed" && sendFixedInstanceId && connectedInstances.length > 0 && (
                        <span className="text-cp-primary-light">
                          {" • "}
                          {connectedInstances.find(i => i.id === sendFixedInstanceId)?.display_name || ""}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Botão de salvar */}
              <button
                type="button"
                onClick={saveSendSettings}
                disabled={savingSendSettings}
                data-testid="save-send-settings-btn"
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-cp-primary py-3.5 text-sm font-bold text-cp-bg shadow-lg transition hover:opacity-95 disabled:opacity-50 sm:w-auto sm:min-w-[240px]"
              >
                {savingSendSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Salvar modo de envio
              </button>
            </section>
          </div>
        )}
      </StepCard>

      <StepCard
        index={3}
        title="Conectar seu checkout"
        subtitle="Cole essa URL no seu painel Kiwify / Hotmart / Cartpanda para receber eventos de PIX."
        done={webhookDone}
        active={whatsappDone}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">URL do webhook (POST JSON)</label>
            <div className="flex items-stretch gap-2">
              <code
                data-testid="webhook-url"
                className="flex-1 bg-cp-bg border border-cp-primary/30 px-3 py-2.5 rounded-md font-mono text-xs text-cp-primary-light overflow-x-auto whitespace-nowrap"
              >
                {tenant.webhook_url}
              </code>
              <button
                onClick={() => copy(tenant.webhook_url)}
                data-testid="copy-webhook-btn"
                className="px-4 bg-cp-primary hover:bg-cp-primary-hover text-cp-bg font-semibold rounded-md inline-flex items-center gap-2 text-sm"
              >
                <Copy className="h-4 w-4" /> Copiar
              </button>
            </div>
          </div>

          <div>
            <div className="text-xs text-slate-400 mb-2">Como configurar na sua plataforma:</div>
            <PlatformGuide />
          </div>

          <details className="group">
            <summary className="text-xs text-slate-400 hover:text-white cursor-pointer inline-flex items-center gap-1 select-none">
              <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
              Ver payload JSON esperado
            </summary>
            <pre className="mt-2 bg-cp-bg border border-white/10 rounded-md p-3 text-[11px] text-slate-300 overflow-x-auto">
              {`{
  "event": "pix.generated",     // ou "pix.paid"
  "external_id": "ORDER_ABC123",
  "customer_name": "Maria Silva",
  "customer_phone": "+5511999999999",
  "amount": 97.00,
  "pix_code": "00020126..."
}`}
            </pre>
          </details>

          <div className="flex items-center gap-2 text-xs pt-2">
            <button
              onClick={regenerateWebhook}
              data-testid="regenerate-webhook-btn"
              className="text-slate-400 hover:text-red-300 inline-flex items-center gap-1"
            >
              <RefreshCw className="h-3 w-3" /> Gerar novo token
            </button>
          </div>
        </div>
      </StepCard>

      {evoDone && whatsappDone && (
        <div className="border border-cp-primary/40 bg-cp-primary/10 rounded-lg p-5 flex items-start gap-3" data-testid="connection-ready">
          <CheckCircle2 className="h-6 w-6 text-cp-primary-light shrink-0" />
          <div>
            <div className="font-heading font-bold text-cp-primary-light">Motor pronto para recuperar vendas</div>
            <div className="text-sm text-cp-primary-light/90 mt-1">
              Copie o webhook acima no seu checkout. Toda venda PIX não paga entra automaticamente no fluxo.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
